// One folder's contents, composed for whichever renderer draws them: the sort
// order, inline naming and the right-click menu from their own hooks, plus the
// props each item takes from them. The renderers keep only their markup.

import type { IDocument, IFolder } from '@apis/documents';
import type { INewFolder } from '../../hooks/useNewFolder';
import type { IRenameInputProps } from '../../RenameInput/RenameInput';
import type { IFileItemProps, IFolderItemProps } from '../itemProps';
import { useTableSort } from './useTableSort';
import { useNameEditing } from './useNameEditing';
import { useTableContextMenu } from './useTableContextMenu';

export interface IDocumentViewModelProps {
  documents: IDocument[];
  folders?: IFolder[]; // Subfolders of the current folder
  /** Every subfolder of the current folder, search aside — the names a new or
   *  renamed folder must not repeat. */
  siblingFolders?: readonly IFolder[];
  parentFolder?: { id: string; name: string } | null; // where back navigates
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onOpenFolder?: (id: string) => void;
  /** Absent when the user may not write documents — and likewise every
   *  other write handler: its menu item is not offered. */
  onRenameDoc?: (id: string, newName: string) => void;
  onDeleteDoc: (id: string) => void;
  onUploadNewVersion?: (id: string) => void;
  onMarkupDoc?: (id: string) => void;
  onRenameFolder?: (id: string, newName: string) => void;
  onDeleteFolder?: (id: string) => void;
  canDelete?: boolean;
  newFolder?: INewFolder | null;
  onStartNewFolder?: () => boolean;
  onCommitNewFolder?: (name: string) => void;
  onCancelNewFolder?: () => void;
  emptyMessage?: string;
  invalidDropId?: string | null;
}

export function useDocumentViewModel({
  documents, folders = [], siblingFolders = [], selectedId, onSelect, onOpenFolder, onRenameDoc, onDeleteDoc, onUploadNewVersion,
  onMarkupDoc, onRenameFolder, onDeleteFolder, canDelete = false, newFolder = null,
  onStartNewFolder, onCommitNewFolder, onCancelNewFolder, invalidDropId,
}: IDocumentViewModelProps) {
  const { handleSort, sorted, sortedFolders } = useTableSort(documents, folders);

  const naming = useNameEditing({
    documents, folders, siblingFolders, onRenameDoc, onRenameFolder,
    onStartNewFolder, onCommitNewFolder, onCancelNewFolder,
  });

  const menu = useTableContextMenu({
    documents, folders, canDelete, onUploadNewVersion, onMarkupDoc, onDeleteDoc, onDeleteFolder,
    onNewFolder: onStartNewFolder ? naming.handleNewFolder : undefined,
    startRename: onRenameDoc ? naming.startRename : undefined,
    startRenameFolder: onRenameFolder ? naming.startRenameFolder : undefined,
  });

  // While a menu is open its target keeps the highlight and hover goes quiet,
  // so the eye never reads a hovered neighbour as the thing being acted on.
  const menuTargetId = menu.ctxMenu && menu.ctxMenu.kind !== 'background' ? menu.ctxMenu.targetId : null;

  const openFolder = (id: string) => onOpenFolder?.(id);

  // Only the item being renamed gets the editor's props.
  function renameOf(isRenaming: boolean, suffix?: string): IRenameInputProps | undefined {
    if (!isRenaming) return undefined;
    return {
      value: naming.renameValue, error: naming.renameError, suffix,
      onChange: naming.changeRename, onCommit: naming.commitRename, onCancel: naming.cancelRename,
    };
  }

  const folderProps = (folder: IFolder): IFolderItemProps => ({
    folder,
    isRejected: folder.id === invalidDropId,
    isMenuTarget: folder.id === menuTargetId,
    rename: renameOf(folder.id === naming.renamingFolderId),
    onOpen: openFolder,
    onContextMenu: menu.openFolderMenu,
  });

  const fileProps = (doc: IDocument): IFileItemProps => ({
    doc,
    isSelected: doc.id === selectedId,
    isMenuTarget: doc.id === menuTargetId,
    isRejected: doc.id === invalidDropId,
    rename: renameOf(doc.id === naming.renamingFileId, naming.renameSuffix),
    onSelect,
    onContextMenu: menu.openRowMenu,
  });

  const newFolderProps = newFolder && {
    newFolder,
    value: naming.newFolderName,
    error: naming.newFolderError,
    onChange: naming.changeNewFolder,
    onCommit: naming.commitNewFolder,
    onCancel: naming.cancelNewFolder,
  };

  const isEmpty = sorted.length === 0 && sortedFolders.length === 0 && !newFolder;

  return { handleSort, sorted, sortedFolders, isEmpty, menu, openFolder, folderProps, fileProps, newFolderProps };
}
